import { Button, Icon } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';

/**
 * La fine barre sombre au-dessus de l'écran de la personne (npc.md §11, M5 ; maquette `Regarder`,
 * variante A) : « Regarder », le NPC en lien, l'expérience et sa version, l'état, le tour, et
 * « Quitter ». 40 px, pour que l'écran garde toute la largeur et tout son thème : on peut la
 * projeter telle quelle pour une démo.
 *
 * C'est la seule surface sombre de l'application et elle l'est exprès, comme le voile des boîtes : le
 * jeton `--bg-inverse` du design system. Rien d'autre de PMS ne se mêle à l'écran de la personne, et
 * rien de la machine ne figure dans la barre - ni étape, ni objectif, ni coût.
 *
 * Sur un téléphone la barre ne garde que l'état, le NPC et le tour, et une croix ; le reste est
 * l'écran de la personne tel qu'elle l'a vu. Ce qui est propre à un mode - le bouton « Revoir en
 * différé » d'un rejeu fini, la seconde rangée de la lecture - arrive par `actions` et `below`.
 *
 * « Quitter » est le bouton blanc du package (`primary`, `xs`) : il tient sur le fond sombre. La croix
 * du téléphone reste dessinée ici : le package n'a pas de bouton icône à encre blanche pour une
 * surface sombre, ni de bouton icône de moins de 44 px.
 */

export interface WatchBarProps {
  /** The NPC, or null once it was deleted for good. */
  npc: { id: string; name: string } | null;
  experience: string;
  /** The state pill: « en direct », « en différé », « fini à 15:31 ». */
  state: { label: string; live: boolean };
  turn: string | null;
  /** « Le rejeu est fini » beside the actions, once it is. */
  note?: string;
  /** What stands before « Quitter »: « Revoir en différé ». */
  actions?: ReactNode;
  /** The second row of the recording: the controls and the line of progress. */
  below?: ReactNode;
  onQuit: () => void;
}

export function WatchBar({
  npc,
  experience,
  state,
  turn,
  note,
  actions,
  below,
  onQuit,
}: WatchBarProps) {
  return (
    <header
      data-testid="regarder-barre"
      data-live={state.live ? 'true' : 'false'}
      className="shrink-0 bg-[var(--bg-inverse)] text-small text-white"
    >
      <div className="flex h-10 items-center gap-3 px-3 sm:px-4">
        <span className="hidden font-semibold text-caption text-white/60 uppercase tracking-widest sm:inline">
          Regarder
        </span>
        {npc === null ? (
          <span className="hidden font-semibold sm:inline">NPC supprimé</span>
        ) : (
          <Link
            to="/admin/npc/$npcId"
            params={{ npcId: npc.id }}
            data-testid="regarder-npc"
            className="hidden truncate font-semibold text-white underline-offset-2 hover:underline sm:inline"
          >
            {npc.name}
          </Link>
        )}
        <span data-testid="regarder-experience" className="hidden text-white/70 sm:inline">
          {experience}
        </span>
        <span
          data-testid="regarder-etat"
          className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 font-semibold text-caption"
        >
          {state.live ? (
            <span aria-hidden="true" className="size-1.5 animate-pulse rounded-full bg-rouge" />
          ) : null}
          {state.label}
        </span>
        {/* On a phone the bar keeps the NPC and the turn: « Marie Curie · tour 7 ». */}
        {npc === null && turn === null ? null : (
          <span data-testid="regarder-tour-court" className="truncate text-white/70 sm:hidden">
            {npc === null ? null : npc.name.split(',')[0]}
            {npc !== null && turn !== null ? ' · ' : null}
            {turn}
          </span>
        )}
        {turn === null ? null : (
          <span data-testid="regarder-tour" className="hidden text-white/70 sm:inline">
            {turn}
          </span>
        )}
        <span className="flex-1" />
        {note === undefined ? null : (
          <span data-testid="regarder-note" className="hidden text-white/80 sm:inline">
            {note}
          </span>
        )}
        {actions}
        <span className="inline-flex max-sm:hidden">
          <Button
            type="button"
            variant="primary"
            size="xs"
            compact
            data-testid="regarder-quitter"
            onClick={onQuit}
          >
            Quitter
          </Button>
        </span>
        <button
          type="button"
          data-testid="regarder-croix"
          aria-label="Quitter"
          onClick={onQuit}
          className="inline-flex size-7 cursor-pointer items-center justify-center rounded-md text-white hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit sm:hidden"
        >
          <Icon name="close" size={16} />
        </button>
      </div>
      {below}
    </header>
  );
}
